/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {startTransition, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import {useQueryClient} from '@tanstack/react-query'
import {
    useCustomerId,
    useShopperCustomersMutation,
    useCustomerType,
    useCustomerOrders,
    useShopperContext,
    useUsid,
    usePromotions,
    useProducts
} from '@salesforce/commerce-sdk-react'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import OptiversalLinks from '../product-detail/partials/optiversal-links'

// Components
import {
    Box,
    Flex,
    SimpleGrid,
    Stack,
    useDisclosure,
    Button,
    Image,
    useStyleConfig,
    Skeleton,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import InfiniteScroll from '../../components/infinite-scroll'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import DropdownRefinements from './partials/dropdown-refinements'
import SelectedRefinements, {getSelectedFilters} from './partials/selected-refinements'
import EmptySearchResults from './partials/empty-results'
import PageHeader from './partials/page-header'
import AbovePageHeader from '@salesforce/retail-react-app/app/pages/product-list/partials/above-page-header'
import PDComponent from '../../components/page-designer/pd-component'
import Breadcrumb from '../../components/breadcrumb'
import RefinementsStyles from './partials/refinements-styles'

// Hooks

import {useSearchParams} from '../../hooks/use-search-params'
import {useSortUrls, useBRSortUrls} from '../../hooks/use-sort-urls'
import useCustomerGroups from '../../hooks/use-customer-groups'
import useEinstein from '../../hooks/use-einstein'
import useActiveData from '../../hooks/use-active-data'
import useSeoCategory from '../../hooks/use-seo-category'
import {BM_FILTER_MAPPING, KS_HOME_HREF, YR_HOME_HREF} from '../../constants'

// Others
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// Constants
import {
    MAX_CACHE_AGE,
    STALE_WHILE_REVALIDATE,
    PRODUCT_LIST_IMAGE_VIEW_TYPE,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
    HIDE_FILTERS,
    PD_PLP_STORY_COMPONENT,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4,
    DEFAULT_SEARCH_PARAMS,
    BR_SORT_OPTIONS
} from '../../constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '../../hooks/use-wish-list'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import useTagManager from '../../hooks/use-tag-manager'

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew']

import {getUrl} from '../../utils/cloudinary-utils'
import {formatCategoryTree} from '../../utils/product-utils'
import PageFooter from './partials/page-footer'
import LoadingSpinner from '../../components/loading-spinner'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMonetate from '../../hooks/use-monetate'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    getTotalQuantity,
    getBasketProductIds,
    getQualifyingProductIds
} from '../../utils/basket-utils'
import GWPMeter from '../../components/promo-callout/gwp'
import {useGlobaleContext} from '../../contexts/globale'
import BMSMMeter from '../../components/promo-callout/bmsm'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getPlpPdComponents} from '../../utils/page-designer-utils'
import ProductFiltersDrawer from './partials/refinements-drawer'
import Sort from './partials/product-sorts'
import Schema from './partials/schema'
import {scheduleAfterPaint, scheduleIdleAnalytics} from '../../utils/common-utils'
import MetaTags from './partials/meta-tags'
import usePageDesigner from '../../hooks/use-page-designer'
import usePromoMeterConfig from '../../hooks/use-promo-meter-config'
import useContentAssetConfig from '../../hooks/use-content-asset-config'
import {useSiteContext} from '../../contexts/site'
import {createProductListAnalyticsTasks, scheduleAnalyticsTasks} from './analytics'

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */

// Module-level cache
const plpHitsCache = {}
const FILTER_COMMIT_DEBOUNCE_MS = 400

const cloneSearchParams = (params = {}) => ({
    ...params,
    refine: Object.entries(params?.refine || {}).reduce((nextRefine, [key, value]) => {
        nextRefine[key] = Array.isArray(value) ? [...value] : value
        return nextRefine
    }, {})
})

const ProductListBelowBreadcrumbsRegionSkeleton = () => {
    return (
        <Box
            data-testid="sf-product-list-below-breadcrumbs-region-skeleton"
            minHeight="54px"
            padding={{base: '0 15px', lg: '0 60px'}}
        >
            <Skeleton height="54px" width="100%" />
        </Box>
    )
}

const ProductListHeaderSkeleton = () => {
    return (
        <Box data-testid="sf-product-list-header-skeleton">
            <Box margin={{base: '15px 15px 5px', lg: '15px 60px 5px'}} minHeight="20px">
                <Skeleton width="100px" height="20px" />
            </Box>
            <Box margin={{base: '0 15px 15px', lg: '0 60px 15px'}} minHeight="35px">
                <Flex alignItems="flex-end" gap="10px" height={{base: '32px', lg: '35px'}}>
                    <Skeleton
                        height={{base: '32px', lg: '35px'}}
                        width={{base: '210px', md: '280px'}}
                    />
                    <Skeleton height="20px" width="60px" marginBottom="3px" />
                </Flex>
            </Box>
        </Box>
    )
}

const ProductList = (props) => {
    // Using destructuring to omit properties we must rename `isLoading` because we use a different
    // `isLoading` later in this function.
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    const {isLoading: _unusedIsLoading, staticContext, ...rest} = props
    const {isOpen, onOpen, onClose} = useDisclosure()
    const isMobile = useBreakpointValue({base: true, md: false})
    const plpRef = useRef()
    const {isYR} = useSiteContext()
    const monetate = useMonetate()
    const tagManager = useTagManager()
    const queryClient = useQueryClient()
    const {isRegistered} = useCustomerType()
    const navigate = useNavigation()
    const location = useLocation()
    const einstein = useEinstein()
    const activeData = useActiveData()
    const {res} = useServerContext()
    const {
        app: {commerceAPI}
    } = getConfig()
    let showFutureCategories = true
    const {hostUrl, storefrontPreferences} = commerceAPI.storefront || {}
    const customerId = useCustomerId()
    const productListStyles = useStyleConfig('ProductListStyles')
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const [optimisticSearchParams, setOptimisticSearchParams] = useState(null)
    const optimisticSearchParamsRef = useRef(null)
    const filterNavigateTimerRef = useRef(null)
    const pendingFilterPathRef = useRef(null)
    const pendingFilterSearchRef = useRef(null)
    const filterPendingIdRef = useRef(0)
    const activeSearchParams = optimisticSearchParams || searchParams

    const setOptimisticFilterParams = useCallback((nextSearchParams) => {
        optimisticSearchParamsRef.current = nextSearchParams
        setOptimisticSearchParams(nextSearchParams)
    }, [])

    useEffect(() => {
        const currentPath = `${location.pathname}${location.search}`

        optimisticSearchParamsRef.current = null
        setOptimisticSearchParams(null)

        if (pendingFilterPathRef.current && currentPath !== pendingFilterPathRef.current) {
            if (filterNavigateTimerRef.current) {
                window.clearTimeout(filterNavigateTimerRef.current)
                filterNavigateTimerRef.current = null
            }

            pendingFilterPathRef.current = null
            pendingFilterSearchRef.current = null
            filterPendingIdRef.current += 1
            setFilterResultPending(false)
            setFiltersLoading(false)
        }
    }, [location.pathname, location.search])

    useEffect(() => {
        return () => {
            if (filterNavigateTimerRef.current) {
                window.clearTimeout(filterNavigateTimerRef.current)
            }
        }
    }, [])

    /**************** Page State ****************/
    const [filtersLoading, setFiltersLoading] = useState(false)
    const [filterResultPending, setFilterResultPending] = useState(false)
    const [wishlistLoading, setWishlistLoading] = useState([])
    const {countryISO, currencyCode} = useGlobaleContext()
    const {data: customerGroupIds} = useCustomerGroups()
    const urlParams = new URLSearchParams(location.search)
    let searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery

    /**************** Mutation Actions ****************/
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    /**************** Query Actions ****************/
    // _refine is an invalid param for useProductSearch, we don't want to pass it to API call
    const {_refine, ...restOfParams} = searchParams
    const orderSearchParams = useSearchParams({limit: 100, offset: 0})
    const {limit, offset} = orderSearchParams[0]
    const {site} = useMultiSite()
    const {usid} = useUsid()
    const {data: shopperContext} = useShopperContext(
        {
            parameters: {
                usid: usid || (!isServer && localStorage.getItem('pdUsid')),
                siteId: site.id
            }
        },
        {
            enabled:
                !isServer &&
                (Boolean(usid) || (!isServer && Boolean(localStorage.getItem('pdUsid'))))
        }
    )

    const {config} = commerceAPI.bloomreach || {}
    let enableBloomreachSearch = null
    let brSortingOptions = BR_SORT_OPTIONS
    try {
        const configAsJson = JSON.parse(config)
        if (configAsJson) {
            brSortingOptions = configAsJson.sortOptions
            enableBloomreachSearch = configAsJson.enableBloomreachSearch
        }
        const storefrontPref = JSON.parse(storefrontPreferences)
        if (storefrontPref) {
            showFutureCategories = storefrontPref.showFutureCategories
        }
    } catch (e) {
        console.log(e)
    }

    // Computed once on mount — useRef ensures re-renders don't recheck sessionStorage
    const isBackNav = useRef(
        onClient()
            ? (() => {
                  try {
                      const raw = sessionStorage.getItem('plp_scroll_pending')
                      if (raw) {
                          const saved = JSON.parse(raw)
                          return saved?.pathname === window.location.pathname
                      }
                  } catch (e) {
                      // ignore
                  }
                  return false
              })()
            : false
    ).current

    const {shouldReserveSpace, promoMeterConfig} = usePromoMeterConfig('promo-meter-skeleton')
    const badgeUrlConfig = useContentAssetConfig('badge-url-config')

    // SEO Category
    const {
        data: seoCategory,
        isLoading,
        isFetched,
        isError,
        isRefetching,
        remainingProducts,
        remainingFetchedProducts,
        remainingProductsError
    } = useSeoCategory(
        {
            urlMapping: {urlSegment: isSearch ? null : location?.pathname + '/'},
            restOfParams: {
                ...restOfParams,
                refine: restOfParams?.refine ? JSON.stringify(restOfParams?.refine, null, 0) : ''
            },
            search: {
                ...restOfParams,
                perPricebook: true,
                allVariationProperties: true,
                allImages: true,
                expand: [
                    'promotions',
                    'variations',
                    'prices',
                    'images',
                    'page_meta_tags',
                    'custom_properties',
                    'availability',
                    'represented_products'
                ],
                refine: _refine,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            },
            bloomReachParams: {
                query: searchQuery,
                effectiveDateTime: shopperContext?.effectiveDateTime,
                enabled: true,
                searchType: searchQuery ? 'keyword' : 'category',
                requestType: 'search', // hardcode
                start: offset,
                // On hard refresh: always start with the default 24 regardless of the URL param.
                rows: isBackNav
                    ? Math.min(parseInt(restOfParams?.limit) || DEFAULT_SEARCH_PARAMS?.limit)
                    : DEFAULT_SEARCH_PARAMS?.limit
            }
        },
        {enabled: true},
        customerGroupIds
    )

    // for bloormeach off we don't load products in batches
    const secondBatchLoading = enableBloomreachSearch
        ? (remainingProducts && !remainingFetchedProducts && !remainingProductsError) || isLoading
        : false

    useEffect(() => {
        if (!filterResultPending) {
            return
        }

        const currentPath = `${location.pathname}${location.search}`

        if (pendingFilterPathRef.current && currentPath !== pendingFilterPathRef.current) {
            return
        }

        if ((isFetched || isError) && !isLoading && !isRefetching && !secondBatchLoading) {
            const pendingId = filterPendingIdRef.current
            scheduleAfterPaint(() => {
                if (pendingId !== filterPendingIdRef.current) {
                    return
                }

                pendingFilterPathRef.current = null
                pendingFilterSearchRef.current = null
                setFilterResultPending(false)
                setFiltersLoading(false)
            })
        }
    }, [
        filterResultPending,
        location.pathname,
        location.search,
        isFetched,
        isError,
        isLoading,
        isRefetching,
        secondBatchLoading
    ])

    const {
        productSearchResult,
        category,
        brResponse,
        brRefinements,
        brKeywordRedirect,
        brAutoCorrectQuery,
        brDidYouMean,
        contentByIndex,
        campaign
    } = seoCategory || {}
    if (category?.c_online === false && !showFutureCategories) {
        // Redirect offline categories to homepage
        navigate(isYR ? YR_HOME_HREF : KS_HOME_HREF)
    }
    useEffect(() => {
        if (
            (productSearchResult?.searchPhraseSuggestions?.c_searchRedirect &&
                !enableBloomreachSearch) ||
            (brKeywordRedirect?.['redirected url'] && enableBloomreachSearch)
        ) {
            window.location.href =
                brKeywordRedirect?.['redirected url'] ||
                productSearchResult?.searchPhraseSuggestions?.c_searchRedirect
        }
    }, [
        productSearchResult?.searchPhraseSuggestions?.c_searchRedirect,
        brKeywordRedirect?.['redirected url']
    ])

    const sortValue = (
        searchParams.sort ||
        productSearchResult?.selectedSortingOption?.id ||
        category?.c_defaultSortingOption ||
        'trending-now'
    )
        .toString()
        .toLowerCase()

    const sortOptions = productSearchResult?.sortingOptions || brSortingOptions

    const sortLabel = sortOptions?.find((o) => o.id === sortValue)?.label || ''

    useEffect(() => {
        window.intersectedPromos = null // refresh this so we'll trigger the promotion impression again.
    }, [searchQuery, category?.c_id])

    useEffect(() => {
        if (category?.c_alternativeUrl) {
            const currentParams = new URLSearchParams(location.search)
            const altUrl = new URL(category.c_alternativeUrl, window.location.origin)
            const altParams = new URLSearchParams(altUrl.search)

            currentParams.forEach((value, key) => {
                if (!altParams.has(key)) {
                    altParams.append(key, value)
                }
            })

            const finalUrl = `${
                altUrl.pathname
            }?${altParams.toString()}` /** Preserve Query Parameters When Replacing Category URL with Alternative URL **/
            navigate(finalUrl)
        }
    }, [category?.id])

    const {data: customer} = useCurrentCustomer()
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const {data: {data: orders} = {}, isLoading: isOrdersLoading} = useCustomerOrders(
        {
            parameters: {customerId, limit, offset}
        },
        {enabled: onClient() && !!customerId && customer?.customerType === 'registered'}
    )

    useEffect(() => {
        if (!isOrdersLoading && plpRef) {
            window.lastOrderDate = orders?.[0]?.creationDate || 'n/a'
            plpRef.current?.setAttribute('data-last-order-date', orders?.[0]?.creationDate || 'n/a')
        }
    }, [isOrdersLoading, plpRef])

    useEffect(() => {
        if (!isWishlistLoading && plpRef) {
            plpRef.current?.setAttribute(
                'data-wishlist-length',
                wishlist?.customerProductListItems?.length || 0
            )
        }
    }, [isWishlistLoading, plpRef])

    useEffect(() => {
        if (!isBasketLoading && plpRef) {
            plpRef.current?.setAttribute(
                'data-basket-items-length',
                basket?.productItems?.length ? JSON.stringify(basket?.productItems) : '[]'
            )
            plpRef.current?.setAttribute(
                'data-basket-gift-length',
                basket?.giftCertificateItems?.length
                    ? JSON.stringify(basket?.giftCertificateItems)
                    : '[]'
            )
        }
    }, [isBasketLoading, plpRef, basket])

    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}

    var changeUpProductID = null

    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }

    const totalQuantity = getTotalQuantity(basket, changeUpProductID)

    const [globalDataLoaded, setGlobalDataLayerLoaded] = useState(false)
    const [dataLayerLoaded, setDataLayerLoaded] = useState(false)
    const debounceRef = useRef(null)
    const gtmEventsScheduledRef = useRef(false)
    const gtmEventsVersionRef = useRef(0)
    const analyticsTasksRef = useRef(null)

    useEffect(() => {
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (secondBatchLoading) {
            return // We'll skip the execution if all the products are not yet fetched
        }

        if (!country) return

        if (
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest') &&
            !isBasketLoading &&
            !globalDataLoaded
        ) {
            if (debounceRef.current) clearTimeout(debounceRef.current)

            debounceRef.current = setTimeout(() => {
                tagManager.globalDataLoad(
                    'PLP',
                    searchQuery ? 'search' : category?.c_name,
                    totalQuantity,
                    basket?.productTotal,
                    customer,
                    city,
                    country,
                    state,
                    searchQuery,
                    productSearchResult?.total
                )
                setGlobalDataLayerLoaded(true)
            }, 2000)
        }
    }, [customer?.customerType, customer?.email, isBasketLoading, cookie, secondBatchLoading])

    useEffect(() => {
        analyticsTasksRef.current?.cancel()
        analyticsTasksRef.current = null
        gtmEventsVersionRef.current += 1
        setGlobalDataLayerLoaded(false)
        setDataLayerLoaded(false)
        gtmEventsScheduledRef.current = false
    }, [
        productSearchResult?.hits?.length,
        productSearchResult?.data?.length,
        category?.c_id,
        searchQuery,
        brResponse?.docs,
        productSearchResult?.selectedRefinements,
        productSearchResult?.selectedSortingOption
    ])

    useEffect(
        () => () => {
            // A route exit keeps the current payload valid, so finish any queued
            // impressions before the PLP is removed.
            analyticsTasksRef.current?.flush()
            analyticsTasksRef.current = null
        },
        []
    )

    useEffect(() => {
        if (
            (customer?.customerType === 'registered' && customer?.email) ||
            customer?.customerType === 'guest'
        ) {
            plpRef.current?.setAttribute(
                'data-is-registered',
                customer?.customerType === 'registered'
            )
            window.birthdayMonth = customer?.c_birthdayMonth
            window.monetatePhilantropyShopper = customer?.c_monetatePhilantropyShopper
        }
    }, [customer?.customerType, customer?.email, plpRef])

    // Page Designer
    const aspectTypeId = 'plp'
    const {data: pageDesigner, isLoading: isPageDesignerLoading} = usePageDesigner(
        {
            isPlp: true,
            pageDesigner: {
                aspectTypeId: aspectTypeId,
                categoryId: category?.id || 'search-results',
                aspectAttributes: category?.id ? JSON.stringify({category: category?.id}) : '{}'
            },
            customerGroupIds: customerGroupIds
        },
        {enabled: !!category?.id}
    )
    let page = pageDesigner?.data?.[0]
    page?.regions?.forEach((r) => (r.data = {...page?.data}))
    const topRegion = page?.regions?.find((region) => region?.id === 'top')
    const belowBreadcrumbsRegion = page?.regions?.find(
        (region) => region?.id === 'belowBreadcrumbs'
    )
    const aboveSeoRegion = page?.regions?.find((region) => region?.id === 'aboveSeo')
    const bottomRegion = page?.regions?.find((region) => region?.id === 'bottom')
    const hasTopRegion = !!topRegion?.components?.length
    const hasBelowBreadcrumbsRegion = !!belowBreadcrumbsRegion?.components?.length
    const shouldReserveTopRegion = !isSearch && hasTopRegion
    const isHeaderSkeletonLoading = !isSearch && !category
    const isBelowBreadcrumbsRegionSkeletonLoading =
        !isSearch && (!category || (!!category?.id && isPageDesignerLoading))

    const plpHitsCacheKey = `${location.pathname}${location.search}`
    const initialHits =
        plpHitsCache[plpHitsCacheKey] || productSearchResult?.hits || productSearchResult?.data
    const [hits, setHitsInternal] = useState(initialHits)
    // Wrap setHits to keep the module-level cache in sync (for InfiniteScroll appends)
    const setHits = useCallback(
        (update) => {
            setHitsInternal((prev) => {
                const next = typeof update === 'function' ? update(prev) : update
                if (next?.length) {
                    plpHitsCache[plpHitsCacheKey] = next
                }
                return next
            })
        },
        [plpHitsCacheKey]
    )
    const [wishlistAnimation, setWishlistAnimation] = useState(false)
    const isFilterAppliedRef = useRef(false)
    const hasHandledInitialPlpResetRef = useRef(false)
    const defaultSortingOption = category?.c_defaultSortingOption
    const promotions = useMemo(
        () =>
            hits?.reduce((acc, hit) => {
                if (hit?.productPromotions) {
                    hit.productPromotions.forEach((promotion) => {
                        if (!acc.some((p) => p.promotionId === promotion.promotionId))
                            acc.push(promotion)
                    })
                }
                return acc
            }, []),
        [hits]
    )

    const triggerMonetateEvent = (productHits) => {
        var breadCrumb = category?.c_parentCategoryTree
            ? formatCategoryTree(category.c_parentCategoryTree)
            : ''
        breadCrumb = breadCrumb || 'Home/' + searchQuery
        if (breadCrumb) {
            breadCrumb = breadCrumb?.split('/')
            breadCrumb[0] = 'Home'
            window.mtBreadcrumb = breadCrumb
            monetate.monetateTrackPage(
                searchQuery ? (productHits ? 'search' : 'nosearch') : 'index',
                breadCrumb,
                plpRef.current?.getAttribute('data-basket-items-length')
                    ? JSON.parse(plpRef.current?.getAttribute('data-basket-items-length'))
                    : [],
                plpRef.current?.getAttribute('data-is-registered') === 'true',
                plpRef.current?.getAttribute('data-wishlist-length') || 0,
                productHits,
                searchQuery,
                null,
                plpRef.current?.getAttribute('data-basket-gift-length')
                    ? JSON.parse(plpRef.current?.getAttribute('data-basket-gift-length'))
                    : []
            )
            window.monetateRef = category?.c_id || searchQuery
        }
    }

    // use initial number of hits and not filtered tiles number
    const noOfTiles = hits?.length
    const totalItems = enableBloomreachSearch ? brResponse?.numFound : productSearchResult?.total
    const hasTotalItems = typeof totalItems === 'number'
    const hasKnownEmptyResults = hasTotalItems && totalItems === 0
    const [showInitialTileSkeletons, setShowInitialTileSkeletons] = useState(
        !initialHits?.length && !hasKnownEmptyResults && !isError
    )

    useEffect(() => {
        if (!hasHandledInitialPlpResetRef.current) {
            hasHandledInitialPlpResetRef.current = true
            return
        }

        const hasResolvedProductResults = Boolean(
            productSearchResult?.hits ||
                productSearchResult?.data ||
                productSearchResult?.total === 0
        )

        if (!isBackNav && !hasResolvedProductResults) {
            setHitsInternal(undefined)
            setShowInitialTileSkeletons(true)
        }
    }, [
        searchQuery,
        location.pathname,
        productSearchResult?.hits,
        productSearchResult?.data,
        productSearchResult?.total
    ])

    useEffect(() => {
        if (
            productSearchResult?.hits ||
            productSearchResult?.data ||
            productSearchResult?.total === 0
        ) {
            var searchResults = productSearchResult.hits || productSearchResult?.data
            searchResults = searchResults?.filter((hit) => {
                const productToCheck = enableBloomreachSearch ? hit : hit?.representedProduct
                const showTile =
                    productToCheck?.c_pdpRestrictionEnabled &&
                    productToCheck?.c_pdpRestrictionGroups
                        ? productToCheck?.c_pdpRestrictionGroups?.find(
                              (group) => customerGroupIds?.customerGroupIds.indexOf(group) > -1
                          )
                        : true

                return showTile
            })

            // This update creates the LCP candidates. Keep it urgent so React does not
            // defer the first product images behind third-party startup work.
            setHits((prevHits) => {
                // Don't overwrite cached hits (from back-nav) with fewer API results
                if (
                    !isFilterAppliedRef.current &&
                    isBackNav &&
                    prevHits?.length > searchResults?.length &&
                    plpHitsCache[plpHitsCacheKey]
                ) {
                    return prevHits
                }
                return searchResults
            })
            setShowInitialTileSkeletons(false)

            if (isFilterAppliedRef.current) {
                window.monetateRef = ''
                isFilterAppliedRef.current = false
            }
            if (
                (window.monetateRef && window.monetateRef === category?.c_id) ||
                (window.monetateRef && window.monetateRef === searchQuery)
            ) {
                return
            }
            if (secondBatchLoading) {
                return // We'll skip the execution if all the products are not yet fetched
            }
            /** NOTE: Not a ReactJS practice, but we need to make sure that the order and the wishlist data is completely fetched and new hits are available. **/
            var CONTROL_INTERVAL_MODULE = setInterval(function () {
                if (
                    (plpRef.current?.getAttribute('data-last-order-date') ||
                        customer?.customerType !== 'registered') &&
                    (plpRef.current?.getAttribute('data-wishlist-length') ||
                        customer?.customerType !== 'registered') &&
                    plpRef.current?.getAttribute('data-basket-items-length') !== '' &&
                    plpRef.current?.getAttribute('data-is-registered')
                ) {
                    // Monetate owns the page reference used by the next PLP event.
                    // Commit it before a filter or sort can navigate to another result set.
                    triggerMonetateEvent(productSearchResult.hits || productSearchResult?.data)
                    clearInterval(CONTROL_INTERVAL_MODULE)
                }
            }, 1000) // check for every 1s

            return () => {
                clearInterval(CONTROL_INTERVAL_MODULE)
            }
        }
    }, [
        productSearchResult?.hits,
        productSearchResult?.data,
        productSearchResult?.query,
        customerGroupIds,
        secondBatchLoading
    ])

    const gtmEvents = () => {
        if (!!customerId && customer?.customerType === 'registered' && isOrdersLoading) {
            return
        }
        if (gtmEventsScheduledRef.current) {
            return
        }

        gtmEventsScheduledRef.current = true
        const scheduledVersion = gtmEventsVersionRef.current

        // 'CookieStorage' takes some miliseconds to fetch cookies, that's why we've used 'useEffect'
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        var breadCrumb = category?.c_parentCategoryTree
            ? formatCategoryTree(category.c_parentCategoryTree)
            : ''

        scheduleIdleAnalytics(async () => {
            if (scheduledVersion !== gtmEventsVersionRef.current) {
                return
            }

            try {
                breadCrumb = breadCrumb ? breadCrumb?.split(' / ').join('/') : ''
                await tagManager.dataLayerLoad(
                    'PLP',
                    isRegistered,
                    breadCrumb,
                    countryISO,
                    currencyCode,
                    customer,
                    city,
                    country,
                    state,
                    category?.c_id,
                    null,
                    category?.c_name || 'search',
                    orders?.length
                )
            } catch (e) {
                await tagManager.dataLayerLoad(
                    'PLP',
                    isRegistered,
                    breadCrumb,
                    null,
                    null,
                    customer,
                    city,
                    country,
                    state,
                    category?.c_id,
                    null,
                    category?.c_name || 'search',
                    orders?.length
                )
            }

            if (scheduledVersion !== gtmEventsVersionRef.current) {
                return
            }

            const analyticsTasks = createProductListAnalyticsTasks({
                hits: productSearchResult.hits || productSearchResult?.data || [],
                breadcrumb: breadCrumb,
                category,
                sortLabel,
                isMobile,
                tagManager
            })
            analyticsTasksRef.current = scheduleAnalyticsTasks(analyticsTasks, {
                shouldRun: () => scheduledVersion === gtmEventsVersionRef.current,
                onComplete: () => {
                    if (scheduledVersion === gtmEventsVersionRef.current) {
                        setDataLayerLoaded(true)
                    }
                }
            })
        })
    }

    useEffect(() => {
        if (
            isFetched &&
            globalDataLoaded &&
            !dataLayerLoaded &&
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest')
        ) {
            gtmEvents()
        }
    }, [
        isFetched,
        globalDataLoaded,
        dataLayerLoaded,
        customer?.customerType,
        customer?.email,
        isOrdersLoading
    ])

    /**************** Response Handling ****************/
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    /**************** Render Variables ****************/
    const basePath = `${location.pathname}${location.search}`
    const showNoResults =
        !isLoading &&
        !showInitialTileSkeletons &&
        ((productSearchResult && !productSearchResult?.hits && !productSearchResult?.total) ||
            isError)
    const {sortingOptions} = productSearchResult || {}

    const brSortUrls = useBRSortUrls({options: brSortingOptions || BR_SORT_OPTIONS})
    const sfccSortUrls = useSortUrls({options: sortingOptions})

    const sortUrls = enableBloomreachSearch ? brSortUrls : sfccSortUrls
    const [wishlistSpinner, setWishlistSpinner] = useState(false)

    // Polling function to make sure customerId & listId are available
    const customerIdRef = useRef(null)
    const listIdRef = useRef(null)

    useEffect(() => {
        if (customerIdRef) {
            customerIdRef.current = customerId
        }
    }, [customerId])
    useEffect(() => {
        if (listIdRef) {
            listIdRef.current = wishlist?.id || null
        }
    }, [wishlist])

    const waitForRefs = async () => {
        const maxRetries = 10
        const interval = 100
        let retries = 0

        return new Promise((resolve, reject) => {
            const checkRefs = () => {
                const listId = listIdRef.current
                if (customerIdRef.current === customerId && listId) {
                    resolve()
                } else if (retries < maxRetries) {
                    retries++
                    setTimeout(checkRefs, interval)
                } else {
                    reject(new Error('Timeout waiting for customerId and listId'))
                }
            }
            checkRefs()
        })
    }

    /**************** Action Handlers ****************/
    const addItemToWishlist = async (index, selectedProductId, selectedProductName) => {
        setWishlistLoading([...wishlistLoading, selectedProductId])
        monetate.setUsedWishlist()
        // TODO: This wishlist object is from an old API, we need to replace it with the new one.
        await waitForRefs()
        const listId = listIdRef.current
        setWishlistSpinner(true)

        try {
            await createCustomerProductListItem.mutate(
                {
                    parameters: {customerId, listId},
                    body: {
                        quantity: 1,
                        public: true,
                        priority: 1,
                        type: 'product',
                        productId: selectedProductId
                    }
                },
                {
                    onSuccess: () => {
                        setWishlistAnimation(index)
                        setWishlistSpinner(false)
                        setTimeout(() => {
                            setWishlistAnimation(false)
                        }, 1000)

                        // EXISTING EVENT
                        tagManager.wishlistInteraction(
                            category?.c_name || '',
                            selectedProductId,
                            selectedProductName,
                            (wishlist?.customerProductListItems?.length || 0) + 1,
                            'add'
                        )

                        // NEW GA4 EVENT
                        try {
                            const plpProduct = productSearchResult?.data?.[index]

                            tagManager.trackGA4Event({
                                eventName: 'add_to_wishlist',
                                itemsAdded: [
                                    {
                                        product: plpProduct
                                    }
                                ],
                                method: searchQuery ? 'site search' : 'plp',
                                currency: currencyCode,
                                sorting: sortLabel || null,
                                listName: searchQuery
                                    ? 'site search'
                                    : `plp - ${category?.c_name || ''}`,
                                listId: searchQuery ? 'site search' : 'plp'
                            })
                        } catch (e) {
                            logger.error('GA4 add_to_wishlist failed', {
                                namespace: 'ProductList.GA4',
                                additionalProperties: {error: e}
                            })
                        }
                    },
                    onSettled: () => {
                        setWishlistSpinner(false)
                        setWishlistLoading(wishlistLoading.filter((id) => id !== selectedProductId))
                    }
                }
            )
        } catch (error) {
            setWishlistSpinner(false)
            logger.error('Error adding product to wishlist', {
                namespace: 'ProductList.handleAddToWishlist',
                additionalProperties: {error, productId: selectedProductId}
            })
        }
    }

    const removeItemFromWishlist = async (index, selectedProductId, selectedProductName) => {
        setWishlistLoading([...wishlistLoading, selectedProductId])
        setWishlistSpinner(true)
        await waitForRefs()

        const listId = listIdRef.current
        const itemId = wishlist.customerProductListItems.find(
            (i) => i.productId === selectedProductId
        ).id
        try {
            await deleteCustomerProductListItem.mutate(
                {
                    body: {},
                    parameters: {customerId, listId, itemId}
                },
                {
                    onError: () => {
                        setWishlistSpinner(false)
                        setWishlistAnimation(false)
                    },
                    onSuccess: () => {
                        tagManager.wishlistInteraction(
                            category?.c_name || '',
                            selectedProductId,
                            selectedProductName,
                            (wishlist?.customerProductListItems?.length || 0) - 1,
                            'remove'
                        )
                        try {
                            const plpProduct = productSearchResult?.data?.[index]

                            tagManager.trackGA4Event({
                                eventName: 'remove_from_wishlist',
                                itemsAdded: [
                                    {
                                        product: plpProduct
                                    }
                                ],
                                currency: currencyCode,
                                sorting: sortLabel || null,
                                method: searchQuery ? 'site search' : 'plp',
                                listName: searchQuery
                                    ? 'site search'
                                    : `plp - ${category?.c_name || ''}`,
                                listId: searchQuery ? 'site search' : 'plp'
                            })
                        } catch (e) {
                            logger.error('GA4 remove_from_wishlist failed', {
                                namespace: 'ProductList.GA4',
                                additionalProperties: {error: e}
                            })
                        }
                        setWishlistSpinner(false)
                        setWishlistAnimation(false)
                    },
                    onSettled: () => {
                        setWishlistSpinner(false)
                        setWishlistLoading(wishlistLoading.filter((id) => id !== selectedProductId))
                    }
                }
            )
        } catch (error) {
            setWishlistSpinner(false)
            logger.error('Error adding product to wishlist', {
                namespace: 'ProductList.removeItemFromWishlist',
                additionalProperties: {error, productId: itemId}
            })
        }
    }

    const mapFacetsToRefinements = (facets, isGlobaleCountry) => {
        return facets
            ?.filter((facet) => {
                // Remove the Price facet if the shopper is in a global country and the search originated from Bloomreach.
                if (
                    isGlobaleCountry &&
                    enableBloomreachSearch &&
                    facet.name?.toLowerCase() === 'price'
                ) {
                    return false
                }
                return true
            })
            .map((facet) => ({
                attributeId: facet.name,
                label: facet.name[0].toUpperCase() + facet.name.slice(1),
                values: facet.value?.map((v) => {
                    if (facet.type === 'number_range') {
                        const val = `[${v.start} TO ${v.end}]`
                        const label =
                            v.start === '*'
                                ? `Below $${v.end}`
                                : v.end === '*'
                                ? `Above $${v.start}`
                                : `$${v.start} - $${v.end}`
                        return {label, value: val, hitCount: v.count}
                    }
                    return {
                        label: v.name || v.cat_name,
                        value: v.name || v.cat_id,
                        hitCount: v.count
                    }
                })
            }))
    }

    const [selectedRefinements, setSelectedRefinements] = useState({})
    useEffect(() => {
        const url = new URL(window.location.href)
        const refinements = {}
        url.searchParams.getAll('refine').forEach((r) => {
            const [k, v] = decodeURIComponent(r).split('=')
            refinements[k] = v
        })
        setSelectedRefinements(refinements)
    }, [productSearchResult, brResponse?.docs])

    const filters = useMemo(() => {
        const refinementSource =
            productSearchResult?.refinements ||
            mapFacetsToRefinements(brRefinements?.facets, countryISO !== 'US') ||
            []

        return refinementSource.filter(
            ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
        )
    }, [
        productSearchResult?.refinements,
        brRefinements?.facets,
        countryISO,
        enableBloomreachSearch
    ])
    const inStockAt = useMemo(
        () => brRefinements?.facets?.find((r) => r?.name === 'in_stock_at')?.value || [],
        [brRefinements?.facets]
    )
    const selectedFilterValues = useMemo(
        () =>
            activeSearchParams?.refine ||
            productSearchResult?.selectedRefinements ||
            selectedRefinements,
        [activeSearchParams?.refine, productSearchResult?.selectedRefinements, selectedRefinements]
    )
    const selectedFilters = useMemo(
        () =>
            selectedFilterValues && filters
                ? getSelectedFilters(filters, selectedFilterValues)
                : [],
        [filters, selectedFilterValues]
    )
    const selectedFiltersByAttribute = useMemo(
        () =>
            selectedFilters?.reduce((acc, {value, apiLabel}) => {
                if (!acc[value]) {
                    acc[value] = new Set()
                }
                acc[value].add(apiLabel)
                return acc
            }, {}),
        [selectedFilters]
    )

    const tagManagerProductFilters = (
        searchParamsCopy,
        isClearAll,
        selectedSortRule,
        attributeId
    ) => {
        var urlSearchParamsCopy = searchParamsCopy || {...searchParams}
        try {
            const filterSelected = isClearAll
                ? ''
                : urlSearchParamsCopy.refine
                ? Object.keys(urlSearchParamsCopy.refine)
                      ?.map((key) => key?.replace(/^c_/, ''))
                      ?.join(', ')
                : ''
            const filterOptionSelected = !isClearAll
                ? urlSearchParamsCopy.refine
                    ? Object.values(urlSearchParamsCopy.refine)
                          .flat()
                          .map((value) => {
                              // Check if the value is a price range and transform it
                              if (value.includes('..')) {
                                  const match = value.match(/\((\d+)\.\.(\d+)\)/)
                                  return match ? `$${match[1]} - $${match[2]}` : value
                              }
                              return value
                          })
                          .join(', ')
                    : ''
                : ''
            var filterSort = selectedSortRule
                ? productSearchResult.sortingOptions?.find((o) => o.id === selectedSortRule)
                      ?.label || brSortingOptions?.find((o) => o.id === selectedSortRule)?.label
                : ''
            if (!filterSort) {
                filterSort = selectedSortRule || productSearchResult?.sortingOptions?.[0]?.label
            }
            if (!filterSort) {
                filterSort = document
                    .querySelector('.product-list-sort select')
                    ?.selectedOptions?.[0]?.innerText?.trim()
            }
            var filterMethod = isOpen ? 'slide out' : 'dropdown'
            const filterValues = isClearAll
                ? ''
                : Object.fromEntries(
                      filters
                          .filter((f) => urlSearchParamsCopy.refine[f.attributeId])
                          .map((f) => [
                              `filter${BM_FILTER_MAPPING?.[f.label] || f.label}`,
                              f.attributeId === 'price'
                                  ? f.values.find(
                                        (v) => v.value === urlSearchParamsCopy.refine[f.attributeId]
                                    )?.label
                                  : typeof urlSearchParamsCopy.refine[f.attributeId] === 'string'
                                  ? urlSearchParamsCopy.refine[f.attributeId]
                                  : urlSearchParamsCopy.refine[f.attributeId].join(', ')
                          ])
                  )
            if (filterValues?.filterStoreStock) {
                filterValues.filterStoreStock =
                    attributeId === 'in_stock_at' || attributeId === 'c_availableInStores'
                        ? 'true'
                        : 'false'
            }
            if (filterValues?.filterIn_stock_at) {
                delete filterValues.filterIn_stock_at
            }
            tagManager.productsFilter({
                event: 'filter',
                filterCategory: '',
                filterStyle: '',
                filterColor: '',
                filterMetal: '',
                filterCollection: '',
                filterPrice: undefined,
                filterMethod: attributeId === 'in_stock_at' ? 'radio' : filterMethod,
                filterSelected: filterSelected
                    ?.replace('in_stock_at', 'In Stock Near Me')
                    ?.replace('c_availableInStores', 'In Stock Near Me'),
                filterOptionSelected: filterOptionSelected,
                filterSort: filterSort,
                ...filterValues
            })
        } catch (e) {
            /* do nothing */
            console.error(e)
        }
    }

    const cancelProductListQueries = useCallback(() => {
        queryClient.cancelQueries({queryKey: ['use-br-search']})
        queryClient.cancelQueries({queryKey: ['use-plp-products-initial']})
        queryClient.cancelQueries({queryKey: ['use-plp-products-remaining']})
        queryClient.cancelQueries({queryKey: ['use-plp-search']})
    }, [queryClient])

    const scheduleFilterNavigation = useCallback(
        (nextPath) => {
            // Cancel before the navigation debounce so stale results cannot repaint the grid.
            cancelProductListQueries()
            pendingFilterPathRef.current = nextPath
            pendingFilterSearchRef.current = nextPath.includes('?')
                ? nextPath.slice(nextPath.indexOf('?'))
                : ''

            if (filterNavigateTimerRef.current) {
                window.clearTimeout(filterNavigateTimerRef.current)
            }

            filterNavigateTimerRef.current = window.setTimeout(() => {
                filterNavigateTimerRef.current = null
                scheduleAfterPaint(() => {
                    startTransition(() => {
                        navigate(nextPath)
                    })
                })
            }, FILTER_COMMIT_DEBOUNCE_MS)
        },
        [cancelProductListQueries, navigate]
    )

    const scheduleFilterAnalytics = useCallback((callback) => {
        // Every filter interaction is an analytics event. Let navigation coalesce rapid
        // UI changes, but keep each captured event and run it outside the click task.
        scheduleIdleAnalytics(callback)
    }, [])

    // Toggles filter on and off
    const toggleFilter = (value, attributeId, selected, allowMultiple = true) => {
        filterPendingIdRef.current += 1
        startTransition(() => {
            setFilterResultPending(true)
            setFiltersLoading(true)
        })

        if (attributeId === 'price') {
            // The destination PLP reads this value when it builds the Monetate page event.
            monetate.setPriceFilter(value)
        }
        if (attributeId === 'c_metal' || attributeId === 'c_classCode') {
            scheduleIdleAnalytics(() => {
                var storefrontValues = [
                    ...new Set(
                        [
                            ...document.querySelectorAll(
                                `div[data-testid='sf-selected-refinements'] button[data-refinement='${attributeId}'`
                            )
                        ]
                            .map((b) => b.dataset.label)
                            .filter(Boolean)
                    )
                ].join(', ')
                if (!selected) {
                    storefrontValues = storefrontValues.split(', ').includes(value.value)
                        ? storefrontValues
                        : storefrontValues
                        ? storefrontValues + ', ' + value.value
                        : value.value
                } else {
                    if (storefrontValues.split(', ')?.length > 1 && attributeId !== 'c_classCode') {
                        storefrontValues = storefrontValues
                            .split(', ')
                            .filter((v) => v !== value.value)
                            .join(', ')
                    }
                    if (attributeId === 'c_classCode') {
                        storefrontValues = storefrontValues
                            .split(', ')
                            .filter((v) => v !== value.value)
                            .join(', ')
                    }
                }
                if (!isServer) {
                    localStorage.setItem('monetate-' + attributeId, storefrontValues)
                }
            }, 0)
        }
        isFilterAppliedRef.current = true
        const searchParamsCopy = cloneSearchParams(
            optimisticSearchParamsRef.current || searchParams
        )

        // Remove the `offset` search param if present.
        delete searchParamsCopy.offset

        // If we aren't allowing for multiple selections, simply clear any value set for the
        // attribute, and apply a new one if required.
        if (!allowMultiple) {
            const previousValue = searchParamsCopy.refine[attributeId]
            delete searchParamsCopy.refine[attributeId]

            // Note the loose comparison, for "string != number" checks.
            if (!selected && value.value != previousValue) {
                searchParamsCopy.refine[attributeId] = value.value
            }
        } else {
            // Get the attibute value as an array.
            let attributeValue = searchParamsCopy.refine[attributeId] || []

            // Ensure that the value is still converted into an array if it's a `string` or `number`.
            if (typeof attributeValue === 'string') {
                attributeValue = attributeValue.split('|')
            } else if (typeof attributeValue === 'number') {
                attributeValue = [attributeValue]
            }

            // Either set the value, or filter the value out.
            if (!selected) {
                if (!attributeValue.some((v) => v == value.value)) {
                    attributeValue.push(value.value)
                }
            } else {
                // Note the loose comparison, for "string != number" checks.
                attributeValue = attributeValue?.filter((v) => v != value.value)
            }

            // Update the attribute value in the new search params.
            searchParamsCopy.refine[attributeId] = attributeValue

            // If the update value is an empty array, remove the current attribute key.
            if (searchParamsCopy.refine[attributeId].length === 0) {
                delete searchParamsCopy.refine[attributeId]
            }
            try {
                /* Safety Checks added for YR-136. */
                if (selected) {
                    // Filter out invalid entries
                    Object.keys(searchParamsCopy?.refine).forEach((key) => {
                        if (Array.isArray(searchParamsCopy?.refine?.[key])) {
                            searchParamsCopy.refine[key] = searchParamsCopy.refine[key].filter(
                                (v) => selectedFiltersByAttribute[key]?.has(v)
                            )
                            if (!searchParamsCopy.refine[key].length)
                                delete searchParamsCopy.refine[key]
                        } else {
                            if (!selectedFiltersByAttribute[key]?.has(searchParamsCopy.refine[key]))
                                delete searchParamsCopy.refine[key]
                        }
                    })
                }
            } catch (e) {
                /* do nothing */
            }
        }
        if (!searchParamsCopy?.sort && !enableBloomreachSearch) {
            if (defaultSortingOption) {
                searchParamsCopy.sort = defaultSortingOption
            } else {
                delete searchParamsCopy.sort
            }
        }
        const nextPath = `${location.pathname}?${stringifySearchParams(searchParamsCopy)}`
        if (allowMultiple) {
            startTransition(() => setOptimisticFilterParams(searchParamsCopy))
        } else {
            // Single-select controls use the rendered selected state on the next click.
            // Commit it urgently so a rapid second click cannot act on stale selection UI.
            setOptimisticFilterParams(searchParamsCopy)
        }
        scheduleFilterNavigation(nextPath)
        scheduleFilterAnalytics(() =>
            tagManagerProductFilters(searchParamsCopy, null, null, attributeId)
        )
    }

    // Clears all filters
    const resetFilters = () => {
        filterPendingIdRef.current += 1
        startTransition(() => {
            setFilterResultPending(true)
            setFiltersLoading(true)
        })

        const newSearchParams = {
            ...cloneSearchParams(optimisticSearchParamsRef.current || searchParams),
            refine: {}
        }
        if (!newSearchParams?.sort) {
            if (defaultSortingOption) {
                newSearchParams.sort = defaultSortingOption
            } else {
                delete newSearchParams.sort
            }
        }
        const newPath = isSearch
            ? `/search?${stringifySearchParams(newSearchParams)}`
            : `${location.pathname}?${stringifySearchParams(newSearchParams)}`

        isFilterAppliedRef.current = true
        startTransition(() => setOptimisticFilterParams(newSearchParams))
        scheduleFilterNavigation(newPath)
        scheduleFilterAnalytics(() => tagManagerProductFilters(null, true))
    }

    /**************** Einstein ****************/
    useEffect(() => {
        if (productSearchResult) {
            if (isSearch) {
                try {
                    einstein.sendViewSearch(searchQuery, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewSearch error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, searchQuery}
                    })
                }
                activeData.sendViewSearch(searchParams, productSearchResult)
            } else if (category?.c_id) {
                try {
                    einstein.sendViewCategory(category, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewCategory error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, category}
                    })
                }
                activeData.sendViewCategory(searchParams, category, productSearchResult)
            }
        }
    }, [productSearchResult, category?.c_id])

    const gridRef = useRef(null)
    const intl = useIntl()

    const {configOptiversal} = commerceAPI.optiversal || {}

    var enabledRelatedLinksPLP = false
    var maxLinksPLP = 10

    try {
        const jsonConfig = JSON.parse(configOptiversal)
        enabledRelatedLinksPLP = jsonConfig.enabledRelatedLinksPLP
        maxLinksPLP = jsonConfig.maxLinksPLP
    } catch (e) {
        console.log(e.message)
    }

    const [isOptiversalLoading, setIsOptiversalLoading] = useState(enabledRelatedLinksPLP)

    const emptyResultsPage =
        onClient() &&
        showNoResults &&
        (!category ||
            (brResponse?.docs?.length
                ? brResponse?.docs?.length
                : productSearchResult?.total === 0))
    const noSrpStyle = emptyResultsPage
        ? {padding: {base: '0 !important', lg: '0 60px 0 60px !important'}}
        : {}
    useEffect(() => {
        const resetStateOnBack = (event) => {
            if (event.persisted) {
                // Page was restored from bfcache (back-forward cache)
                setWishlistAnimation(false)
                setWishlistLoading(false)
                setFiltersLoading(false)
            }
        }
        window.addEventListener('pageshow', resetStateOnBack)
        // This will ensure if modal is opened on PDP and back button is clicked, PLP will be scrollable.
        document.querySelector('html').style.overflow = ''
        document.querySelector('html').classList.remove('block-scroll')
        return () => {
            window.removeEventListener('pageshow', resetStateOnBack)
        }
    }, [])

    useEffect(() => {
        if (emptyResultsPage && !searchQuery) {
            monetate.track404Page()
        }
    }, [emptyResultsPage, searchQuery])

    // Scroll position restoration on back-navigation from PDP
    const scrollRetryTimer = useRef(null)
    const scrollTargetY = useRef(null)
    const isRestoringScroll = useRef(false)

    useEffect(() => {
        const STORAGE_KEY = 'plp_scroll_pending'

        // check if we should restore scroll position after back-navigation.
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY)
            if (raw) {
                const saved = JSON.parse(raw)
                if (saved?.pathname === window.location.pathname) {
                    scrollTargetY.current = saved.scrollY
                    isRestoringScroll.current = true
                    // Attempt immediate scroll — if react-query cache has products,
                    window.scrollTo({top: saved.scrollY, behavior: 'instant'})
                }
                sessionStorage.removeItem(STORAGE_KEY)
            }
        } catch (e) {
            console.log(e.message)
        }

        // Save scroll position — throttled via rAF so sessionStorage.setItem runs at most
        // once per animation frame (~16ms) rather than on every scroll event.
        let rafScheduled = false
        const saveScrollPosition = () => {
            if (isRestoringScroll.current) return
            if (rafScheduled) return
            rafScheduled = true
            requestAnimationFrame(() => {
                rafScheduled = false
                try {
                    sessionStorage.setItem(
                        STORAGE_KEY,
                        JSON.stringify({
                            scrollY: window.scrollY,
                            pathname: window.location.pathname
                        })
                    )
                } catch (e) {
                    console.warn(e.message)
                }
            })
        }
        window.addEventListener('scroll', saveScrollPosition, {passive: true})

        // Clear on refresh/tab close (beforeunload fires on full-page events, NOT SPA navigations)
        const onBeforeUnload = () => {
            sessionStorage.removeItem(STORAGE_KEY)
        }
        window.addEventListener('beforeunload', onBeforeUnload)

        return () => {
            window.removeEventListener('scroll', saveScrollPosition)
            window.removeEventListener('beforeunload', onBeforeUnload)
            if (scrollRetryTimer.current) clearTimeout(scrollRetryTimer.current)
        }
    }, [])

    // Attempt to scroll to saved position whenever hits update.
    useEffect(() => {
        if (!hits?.length || scrollTargetY.current === null) return
        const targetY = scrollTargetY.current

        if (scrollRetryTimer.current) clearTimeout(scrollRetryTimer.current)

        const MAX_RETRIES = 40 // ~10 seconds
        const RETRY_INTERVAL = 250
        let retries = 0
        const attemptScroll = () => {
            window.scrollTo({top: targetY, behavior: 'instant'})
            if (Math.abs(window.scrollY - targetY) > 50 && retries < MAX_RETRIES) {
                retries++
                scrollRetryTimer.current = setTimeout(attemptScroll, RETRY_INTERVAL)
            } else {
                scrollTargetY.current = null
                scrollRetryTimer.current = null
                isRestoringScroll.current = false
            }
        }
        requestAnimationFrame(() => requestAnimationFrame(attemptScroll))
    }, [hits])

    const plpPdComponents = useMemo(() => getPlpPdComponents(), [])
    const [tilesSkeletonCount, setTilesSkeletonCount] = useState(secondBatchLoading ? 12 : 0)
    useEffect(() => {
        setTilesSkeletonCount(secondBatchLoading ? 12 : 0)
    }, [secondBatchLoading])
    const showTilesSkeleton = tilesSkeletonCount > 0

    // Keep below-grid content in the layout, but do not paint it while the grid is
    // changing. This prevents those sections from becoming visible CLS targets as
    // initial results or either Bloomreach "View More" batch replaces skeletons.
    const isGridContentLoading =
        isLoading ||
        filterResultPending ||
        showInitialTileSkeletons ||
        showTilesSkeleton ||
        secondBatchLoading ||
        (!isSearch && isPageDesignerLoading)
    const isPlpContentLoading =
        isGridContentLoading || (enabledRelatedLinksPLP && isOptiversalLoading)

    useEffect(() => {
        if (!onClient() || !isGridContentLoading) {
            return
        }

        let animationFrame
        const clampScrollToGrid = () => {
            if (animationFrame) {
                return
            }

            animationFrame = window.requestAnimationFrame(() => {
                animationFrame = null
                const grid = gridRef.current
                if (!grid) {
                    return
                }

                const gridBottom = window.scrollY + grid.getBoundingClientRect().bottom
                const maximumScrollY = Math.max(0, gridBottom - window.innerHeight)

                if (window.scrollY > maximumScrollY) {
                    window.scrollTo({top: maximumScrollY, behavior: 'auto'})
                }
            })
        }

        clampScrollToGrid()
        window.addEventListener('scroll', clampScrollToGrid, {passive: true})
        window.addEventListener('resize', clampScrollToGrid)

        return () => {
            window.removeEventListener('scroll', clampScrollToGrid)
            window.removeEventListener('resize', clampScrollToGrid)
            if (animationFrame) {
                window.cancelAnimationFrame(animationFrame)
            }
        }
    }, [isGridContentLoading])

    const handleOpen = useCallback(() => {
        scheduleAfterPaint(() => {
            startTransition(() => {
                onOpen()
            })
        })
    }, [onOpen])

    const handleClose = useCallback(() => {
        scheduleAfterPaint(() => {
            startTransition(() => {
                onClose()
            })
        })
    }, [onClose])

    const meterPromos = useMemo(
        () =>
            promotions
                ?.filter(function (promo) {
                    return (
                        promo?.promotionId.indexOf('GWP') > -1 ||
                        promo?.promotionId.indexOf('PWP') > -1 ||
                        promo?.promotionId.indexOf('BMSM') > -1
                    )
                })
                .map(function (promo) {
                    return promo?.promotionId
                }),
        [promotions]
    )

    const enabledPromotion = !isServer && !!meterPromos
    const {data: promotionsDetails, isLoading: isPromoLoading} = usePromotions(
        {
            parameters: {
                ids: [meterPromos]
            }
        },
        {
            enabled: enabledPromotion
        }
    )

    const details = promotionsDetails?.data?.find(function (data) {
        return data?.c_bmsmAndGwpMeterConfig && !!data?.c_enableProgressMeter
    })

    const promoMeterType =
        details?.id?.indexOf('GWP') > -1 || details?.id?.indexOf('PWP') > -1
            ? 'GWP'
            : details?.id?.indexOf('BMSM') > -1
            ? 'BMSM'
            : null
    const initialTileSkeletonCount =
        !enableBloomreachSearch || isBackNav
            ? Number(searchParams.limit) || DEFAULT_SEARCH_PARAMS.limit
            : DEFAULT_SEARCH_PARAMS.limit
    const initialTileSkeletons = new Array(initialTileSkeletonCount)
        .fill(0)
        .map((value, index) => <ProductTileSkeleton key={index} />)

    const basketProductIds = useMemo(() => getBasketProductIds(basket), [basket?.productItems])
    const shouldFetchBasketProducts =
        !isServer && promoMeterType === 'GWP' && Boolean(details?.id && basketProductIds.length)
    const {data: basketProductsData, isLoading: isQualifyingProductsLoading} = useProducts(
        {
            parameters: {
                ids: basketProductIds.join(','),
                expand: ['promotions'],
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: shouldFetchBasketProducts
        }
    )
    const gwpQualifyingProductIds = useMemo(
        () => getQualifyingProductIds(basketProductsData?.data, details?.id),
        [basketProductsData?.data, details?.id]
    )

    return (
        <Box
            className="sf-product-list-page"
            sx={{
                '& .wishlist-wrapper': {
                    display: countryISO === 'US' || countryISO === '' ? 'block' : 'none'
                },
                '& > .sf-breadcrumb': {
                    marginBottom: '5px'
                }
            }}
            ref={plpRef}
            data-type={searchQuery ? 'srp' : 'clp'} // We're using this in GTM events
            data-wishlist-length=""
            data-last-order-date=""
            data-basket-items-length=""
            data-basket-gift-length=""
            data-is-registered=""
            data-grid-loading={isPlpContentLoading}
            aria-busy={isPlpContentLoading}
            data-testid="sf-product-list-page"
            paddingTop="0"
            {...productListStyles.page}
            {...noSrpStyle}
        >
            <Schema products={hits} />
            <MetaTags
                enableBloomreachSearch={enableBloomreachSearch}
                category={category}
                hostUrl={hostUrl}
                productSearchResult={productSearchResult}
            />
            {wishlistSpinner && <LoadingSpinner position="fixed" top="auto" />}
            {emptyResultsPage ||
            (!enableBloomreachSearch &&
                productSearchResult?.searchPhraseSuggestions?.c_searchRedirect) ||
            (enableBloomreachSearch && brKeywordRedirect?.['redirected url']) ? (
                (!enableBloomreachSearch &&
                    !productSearchResult?.searchPhraseSuggestions?.c_searchRedirect) ||
                (enableBloomreachSearch && !brKeywordRedirect?.['redirected url']) ? (
                    <EmptySearchResults searchQuery={searchQuery} />
                ) : (
                    <LoadingSpinner />
                )
            ) : (
                <>
                    <AbovePageHeader />

                    {shouldReserveTopRegion ? (
                        <Box minHeight="54px">
                            {hasTopRegion && (
                                <PDComponent
                                    parent={true}
                                    page={{...page.data, ...topRegion}}
                                    dataLayerLoaded={dataLayerLoaded}
                                />
                            )}
                        </Box>
                    ) : (
                        <></>
                    )}

                    {campaign?.htmlText && enableBloomreachSearch && (
                        <Box
                            className="br-campaign-wrapper"
                            dangerouslySetInnerHTML={{__html: campaign?.htmlText}}
                        />
                    )}

                    {isHeaderSkeletonLoading ? (
                        <ProductListHeaderSkeleton />
                    ) : (
                        <>
                            {/* Breadcrumb */}
                            {!isSearch && (
                                <Breadcrumb categories={category?.c_parentCategoryTree} />
                            )}
                            <PageHeader
                                searchQuery={searchQuery}
                                category={category}
                                productSearchResult={productSearchResult}
                                brResponse={brResponse}
                                enableBloomreachSearch={enableBloomreachSearch}
                                brAutoCorrectQuery={brAutoCorrectQuery}
                                brDidYouMean={brDidYouMean}
                                isLoading={isLoading}
                                totalItems={totalItems}
                            />
                        </>
                    )}

                    {!isSearch &&
                        !category?.c_hideGwpBmsmMeter &&
                        shouldReserveSpace &&
                        (promoMeterConfig?.type === 'GWP' || promoMeterConfig?.type === 'BMSM') &&
                        (isPromoLoading || !details) && (
                            <Box margin={{base: '15px 15px', lg: '15px 60px 0'}}>
                                <Skeleton
                                    height={{
                                        base: promoMeterConfig?.type === 'GWP' ? '218px' : '113px',
                                        md: promoMeterConfig?.type === 'GWP' ? '114px' : '78px'
                                    }}
                                    width="100%"
                                    borderRadius="10px"
                                    maxWidth="900px"
                                />
                            </Box>
                        )}

                    {!isSearch && !category?.c_hideGwpBmsmMeter && promoMeterType === 'GWP' && (
                        <GWPMeter
                            basket={basket}
                            isBasketLoading={isBasketLoading}
                            hits={hits}
                            dataLayerLoaded={dataLayerLoaded}
                            size="plp"
                            promoDetail={details}
                            isPromoLoading={isPromoLoading}
                            qualifyingProductIds={gwpQualifyingProductIds}
                            isQualifyingProductsLoading={isQualifyingProductsLoading}
                        />
                    )}

                    {!isSearch && !category?.c_hideGwpBmsmMeter && promoMeterType === 'BMSM' && (
                        <BMSMMeter
                            size="plp"
                            hits={hits}
                            dataLayerLoaded={dataLayerLoaded}
                            promoDetail={details}
                            isPromoLoading={isPromoLoading}
                        />
                    )}

                    {isBelowBreadcrumbsRegionSkeletonLoading ? (
                        <ProductListBelowBreadcrumbsRegionSkeleton />
                    ) : hasBelowBreadcrumbsRegion ? (
                        <Stack marginBottom="0" gap="0">
                            <PDComponent
                                parent={true}
                                page={{...page.data, ...belowBreadcrumbsRegion}}
                                dataLayerLoaded={dataLayerLoaded}
                            />
                        </Stack>
                    ) : (
                        <></>
                    )}

                    <Stack
                        id="refine-sort-wrapper"
                        position="sticky"
                        top="-14px"
                        backgroundColor="white"
                        margin="0px"
                        padding="1rem 1rem 0.9rem 1rem"
                        {...productListStyles.body.refineSortWrapper.main}
                    >
                        <RefinementsStyles gridRef={gridRef} />
                        <Flex {...productListStyles.body.refineSortWrapper.flex}>
                            <Button
                                aria-label={intl.formatMessage({
                                    id: 'product_list.modal.title.filter.and.sort',
                                    defaultMessage: 'Filter & Sort'
                                })}
                                {...productListStyles.body.refineSortWrapper.button}
                                onClick={handleOpen}
                            >
                                <Image
                                    alt=""
                                    aria-hidden="true"
                                    width="18px"
                                    height="18px"
                                    src={getUrl('UIImages/images/filter-icon.svg')}
                                    {...productListStyles.body.refineSortWrapper.filterIcon}
                                />
                                <Box
                                    {...productListStyles.body.refineSortWrapper.filter}
                                    className="all-filters"
                                    margin="0"
                                >
                                    <FormattedMessage
                                        defaultMessage="Filter & Sort"
                                        id="product_list.modal.title.filter.and.sort"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    />
                                    {selectedFilters?.length ? (
                                        <Box
                                            {...productListStyles.body.refineSortWrapper
                                                .selectedFilters}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            {selectedFilters?.length}
                                        </Box>
                                    ) : (
                                        <></>
                                    )}
                                </Box>
                            </Button>
                        </Flex>
                    </Stack>

                    {selectedFilters?.length ? (
                        <Box {...productListStyles.body.refineSortWrapper.selectedRefinements.main}>
                            <SelectedRefinements
                                filters={filters}
                                selectedFilterValues={selectedFilterValues}
                                toggleFilter={toggleFilter}
                                handleReset={resetFilters}
                            />
                        </Box>
                    ) : (
                        <></>
                    )}
                    {/* Body  */}
                    {filtersLoading && !isOpen && <LoadingSpinner position="fixed" top="auto" />}
                    <Box
                        {...productListStyles.body.main}
                        id="refinement-sort-section"
                        className="refinement-sort-section"
                        padding={{base: 0, lg: '15px 60px'}}
                        margin={{base: 0}}
                    >
                        <Stack
                            display={{base: 'none', lg: 'block'}}
                            width="100%"
                            alignContent="center"
                        >
                            <DropdownRefinements
                                itemsBefore={[
                                    <Flex
                                        key="desktop-sort"
                                        alignItems="center"
                                        flexShrink="0"
                                        marginRight="15px"
                                    >
                                        <Sort
                                            sortUrls={sortUrls}
                                            productSearchResult={productSearchResult}
                                            basePath={basePath}
                                            brSortingOptions={brSortingOptions}
                                            tagManagerProductFilters={tagManagerProductFilters}
                                            isMenu={false}
                                            searchParams={searchParams}
                                            defaultSortingOption={defaultSortingOption}
                                            sortSection="desktop"
                                        />
                                    </Flex>
                                ]}
                                isLoading={filtersLoading}
                                onOpen={handleOpen}
                                marginBottom="1rem"
                                toggleFilter={toggleFilter}
                                filters={filters}
                                excludedFilters={HIDE_FILTERS}
                                selectedFilters={activeSearchParams.refine}
                            />
                        </Stack>
                    </Box>
                    {selectedFilters?.length ? (
                        <Box display={{base: 'none', lg: 'block'}}>
                            <SelectedRefinements
                                filters={filters}
                                selectedFilterValues={selectedFilterValues}
                                toggleFilter={toggleFilter}
                                handleReset={resetFilters}
                            />
                        </Box>
                    ) : (
                        <></>
                    )}
                    <Box>
                        <SimpleGrid
                            id="product-grid"
                            className="product-grid"
                            ref={gridRef}
                            {...productListStyles.body.productsGrid}
                        >
                            {showInitialTileSkeletons
                                ? initialTileSkeletons
                                : hits?.map((productSearchItem, index) => {
                                      const prioritizeImage = index < (isMobile ? 2 : 4)
                                      const selectableAttributeId =
                                          productSearchItem?.variationAttributes?.find((attr) =>
                                              [
                                                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
                                                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
                                                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
                                                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
                                              ]?.includes(attr?.id)
                                          )?.id
                                      // Combine interruptors with the product tile
                                      const gridRegion = page?.regions
                                          ?.find((region) => region?.id === 'main')
                                          ?.components?.find(
                                              (component) =>
                                                  component.typeId === 'dynamic.productList'
                                          )
                                          ?.regions?.find(
                                              (region) => region?.id === `gridRegion${index}`
                                          )
                                      const slotContent = contentByIndex?.[index]
                                      const productKey =
                                          productSearchItem.productId || productSearchItem.id
                                      return (
                                          <React.Fragment key={`fragment-${productKey}`}>
                                              {gridRegion?.components?.map(
                                                  (component, interruptorIndex) => {
                                                      if (
                                                          plpPdComponents?.indexOf(
                                                              component.typeId
                                                          ) !== -1
                                                      ) {
                                                          return (
                                                              <Box
                                                                  key={`full-width-${interruptorIndex}-${component.typeId}-${index}`}
                                                                  data-typeid={component.typeId}
                                                                  width="100%"
                                                                  textAlign="center"
                                                                  mb={4}
                                                                  style={{gridColumn: '1 / -1'}}
                                                              >
                                                                  {page && (
                                                                      <PDComponent
                                                                          page={page}
                                                                          parent={true}
                                                                          component={component}
                                                                          dataLayerLoaded={
                                                                              dataLayerLoaded
                                                                          }
                                                                          id={component.id}
                                                                      />
                                                                  )}
                                                              </Box>
                                                          )
                                                      }
                                                      if (
                                                          component.typeId ===
                                                          PD_PLP_STORY_COMPONENT
                                                      ) {
                                                          return (
                                                              <Box
                                                                  key={`plp-story-${interruptorIndex}-${component.typeId}-${index}`}
                                                                  data-component-size={
                                                                      component?.componentSize
                                                                  }
                                                                  data-typeid={component.typeId}
                                                                  width="100%"
                                                                  textAlign="center"
                                                                  mb={4}
                                                                  gridColumn={{
                                                                      base:
                                                                          component?.data
                                                                              ?.componentSize ===
                                                                          'large'
                                                                              ? 'span 2'
                                                                              : 'span 1',
                                                                      md:
                                                                          component?.data
                                                                              ?.componentSize ===
                                                                          'large'
                                                                              ? 'span 2'
                                                                              : 'span 1'
                                                                  }}
                                                              >
                                                                  {page && (
                                                                      <PDComponent
                                                                          page={page}
                                                                          parent={true}
                                                                          component={component}
                                                                          dataLayerLoaded={
                                                                              dataLayerLoaded
                                                                          }
                                                                          id={component.id}
                                                                          regions={page?.regions[2]}
                                                                      />
                                                                  )}
                                                              </Box>
                                                          )
                                                      }
                                                  }
                                              )}
                                              <ProductTile
                                                  key={`product-tile-${productKey}`}
                                                  sortLabel={sortLabel}
                                                  dataTestid={`sf-product-tile-${productSearchItem.productId}`}
                                                  className="plp-product-tile"
                                                  tilesTitle={
                                                      category?.id
                                                          ? 'plp-' + category?.id
                                                          : 'site search'
                                                  }
                                                  dataLayerLoaded={dataLayerLoaded}
                                                  isBloomReachSearch={enableBloomreachSearch}
                                                  product={productSearchItem}
                                                  category={category}
                                                  isplp={true}
                                                  wishlistAnimation={wishlistAnimation}
                                                  index={index}
                                                  enableFavourite={true}
                                                  wishlist={wishlist}
                                                  isRefreshingData={isRefetching && isFetched}
                                                  imageViewType={PRODUCT_LIST_IMAGE_VIEW_TYPE}
                                                  selectableAttributeId={selectableAttributeId}
                                                  showBadge={true}
                                                  badgeUrlConfig={badgeUrlConfig}
                                                  showTextBadges={true}
                                                  showPromotions={true}
                                                  selectedCurrency={currencyCode}
                                                  selectedCountry={countryISO}
                                                  onClick={() => {
                                                      if (searchQuery) {
                                                          einstein.sendClickSearch(
                                                              searchQuery,
                                                              productSearchItem
                                                          )
                                                      } else if (category) {
                                                          einstein.sendClickCategory(
                                                              category,
                                                              productSearchItem
                                                          )
                                                      }
                                                  }}
                                                  onFavouriteToggle={(
                                                      toBeFavourite,
                                                      selectedProductId,
                                                      selectedProductName
                                                  ) => {
                                                      const action = toBeFavourite
                                                          ? addItemToWishlist
                                                          : removeItemFromWishlist
                                                      return action(
                                                          index,
                                                          selectedProductId,
                                                          selectedProductName
                                                      )
                                                  }}
                                                  dynamicImageProps={{
                                                      imageProps: {
                                                          loading: prioritizeImage
                                                              ? 'eager'
                                                              : 'lazy',
                                                          fetchPriority: prioritizeImage
                                                              ? 'high'
                                                              : 'auto'
                                                      },
                                                      widths: [
                                                          '225w',
                                                          '325w',
                                                          '384w',
                                                          '576w',
                                                          '650w',
                                                          '768w',
                                                          '960w',
                                                          '1250w'
                                                      ]
                                                  }}
                                              />
                                              {slotContent && (
                                                  <Box
                                                      key={`br-slot-${index}`}
                                                      data-query={searchQuery || ''}
                                                      data-category={category?.id || ''}
                                                      className={`br-slot`}
                                                      id={`br-slot-${index}`}
                                                      dangerouslySetInnerHTML={{
                                                          __html: slotContent
                                                      }}
                                                  />
                                              )}
                                          </React.Fragment>
                                      )
                                  })}
                            {!showInitialTileSkeletons && showTilesSkeleton ? (
                                new Array(tilesSkeletonCount)
                                    .fill(0)
                                    .map((value, index) => (
                                        <ProductTileSkeleton
                                            key={`skeleton-slot-${(hits?.length || 0) + index}`}
                                        />
                                    ))
                            ) : (
                                <></>
                            )}
                        </SimpleGrid>
                        {/* Footer */}
                        <InfiniteScroll
                            setHits={setHits}
                            previousHits={hits}
                            isGridLoading={isGridContentLoading}
                            category={category}
                            triggerMonetateEvent={(productHits) => {
                                triggerMonetateEvent(productHits)
                            }}
                            setTilesSkeletonCount={setTilesSkeletonCount}
                            totalItems={totalItems}
                            hitsLength={noOfTiles}
                            searchParams={searchParams}
                            bloomReachParams={{
                                query: searchQuery,
                                effectiveDateTime: shopperContext?.effectiveDateTime,
                                enabled: true,
                                searchType: searchQuery ? 'keyword' : 'category',
                                requestType: 'search', // hardcode
                                rows: DEFAULT_SEARCH_PARAMS?.limit, // This should not exceed 24
                                c_selectedCountry: countryISO,
                                c_selectedCurrency: currencyCode
                            }}
                            noOfTiles={noOfTiles}
                        />
                    </Box>

                    <Box
                        data-testid="sf-product-list-below-grid-content"
                        visibility={isGridContentLoading ? 'hidden' : 'visible'}
                        pointerEvents={isGridContentLoading ? 'none' : 'auto'}
                        aria-hidden={isGridContentLoading}
                    >
                        <Box
                            className="iadvize-conversation-starters-wrapper"
                            padding={{
                                base: '0px 15px 0px 15px',
                                md: '0px 60px 0px',
                                lg: '0px 60px 0px',
                                xl: '0px 180px 0px'
                            }}
                        >
                            <Box id="iadvize-conversation-starters-plp"></Box>
                        </Box>

                        {aboveSeoRegion && (
                            <Box width="100%" marginTop={{base: '15px', md: '30px'}}>
                                <PDComponent
                                    parent={true}
                                    page={{...page.data, ...aboveSeoRegion}}
                                    dataLayerLoaded={dataLayerLoaded}
                                />
                            </Box>
                        )}

                        {category?.c_bottomCategoryHeading &&
                            category?.c_bottomCategoryDescription && (
                                <PageFooter category={category} />
                            )}

                        {bottomRegion && (
                            <Box width="100%" marginTop={{base: '15px', md: '30px'}}>
                                <PDComponent
                                    parent={true}
                                    page={{...page.data, ...bottomRegion}}
                                    dataLayerLoaded={dataLayerLoaded}
                                />
                            </Box>
                        )}
                    </Box>
                </>
            )}
            <ProductFiltersDrawer
                isOpen={isOpen}
                onClose={handleClose}
                filters={filters}
                inStockAt={inStockAt}
                filtersLoading={filtersLoading}
                toggleFilter={toggleFilter}
                resetFilters={resetFilters}
                productSearchResult={productSearchResult}
                brResponse={brResponse}
                sortUrls={sortUrls}
                brSortingOptions={brSortingOptions}
                tagManagerProductFilters={tagManagerProductFilters}
                searchParams={activeSearchParams}
                defaultSortingOption={defaultSortingOption}
                basePath={basePath}
                productListStyles={productListStyles}
            />
            {enabledRelatedLinksPLP &&
                ((!enableBloomreachSearch &&
                    !productSearchResult?.searchPhraseSuggestions?.c_searchRedirect) ||
                    (enableBloomreachSearch && !brKeywordRedirect?.['redirected url'])) && (
                    <Box
                        className="optiversal-link-wrapper"
                        minHeight={{base: '125px', md: '125px'}}
                        visibility={isGridContentLoading ? 'hidden' : 'visible'}
                        pointerEvents={isGridContentLoading ? 'none' : 'auto'}
                        aria-hidden={isGridContentLoading}
                    >
                        <OptiversalLinks
                            query={category?.name || category?.c_name || searchQuery}
                            isplp={true}
                            maxLinks={maxLinksPLP}
                            queryType="topic"
                            onLoadingChange={setIsOptiversalLoading}
                        />
                    </Box>
                )}
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.propTypes = {
    onAddToWishlistClick: PropTypes.func,
    onRemoveWishlistClick: PropTypes.func,
    category: PropTypes.object
}

export default ProductList
